// Snowflake Account API Service
// This file defines the API contract for Snowflake account management including managed keys

import axios from 'axios';

/**
 * Request payload for creating a managed key Snowflake account
 * Sent to: POST /api/v1/snowflake/account with type: "managed_pkey"
 *
 * Backend generates RSA key pair and stores private key securely
 */
export interface ManagedKeyAccountRequest {
  type: 'managed_pkey';
  account: string;
  username: string;
  warehouse: string;
  role?: string;
}

/**
 * Response from managed key account creation
 * Expected from: POST /api/v1/snowflake/account
 *
 * Returns public key for user to add to Snowflake
 */
export interface ManagedKeyAccountResponse {
  account_id: number;
  public_key: string;
  public_key_fingerprint: string;
}

/**
 * Response from getting managed key account details
 * Expected from: GET /api/v1/snowflake/managed-key-account/{id}
 *
 * Used for state restoration when users return to the onboarding flow
 */
export interface ManagedKeyAccountDetailsResponse {
  account_id: number;
  account: string;
  username: string;
  warehouse: string;
  role: string | null;
  public_key: string;
  public_key_fingerprint: string;
}

/**
 * Request payload for confirming managed key setup
 * Sent to: POST /api/v1/snowflake/account/{id}/confirm
 *
 * Tests connection after user has added public key to Snowflake
 */
export interface ConfirmManagedKeyRequest {
  account_id: number;
}

/**
 * Response from managed key confirmation
 * Expected from: POST /api/v1/snowflake/account/{id}/confirm
 *
 * Returns status indicating success or failure of connection test
 */
export interface ConfirmManagedKeyResponse {
  status: 'success' | 'error';
  message?: string;
}

/**
 * Request payload for rotating managed keys
 * Sent to: POST /api/v1/snowflake/account/{id}/rotate-keys
 *
 * Generates new RSA key pair and returns new public key
 */
export interface RotateManagedKeysRequest {
  account_id: number;
}

/**
 * Response from key rotation
 * Expected from: POST /api/v1/snowflake/account/{id}/rotate-keys
 */
export interface RotateManagedKeysResponse {
  account_id: number;
  public_key: string;
  public_key_fingerprint: string;
}

/**
 * Response from account validation
 * Expected from: GET /api/v1/snowflake/validate-account/{identifier}
 */
export interface ValidateAccountResponse {
  valid: boolean;
  message: string;
}

/**
 * Request payload for updating managed key account
 * Sent to: PUT /api/v1/snowflake/account/{id}
 *
 * Updates account object names without regenerating keys
 */
export interface UpdateManagedKeyAccountRequest {
  type: 'managed_pkey';
  username?: string;
  warehouse?: string;
  role?: string;
}

/**
 * Validation error detail from FastAPI/Pydantic
 */
export interface ValidationErrorDetail {
  type: string;
  loc: string[];
  msg: string;
}

/**
 * API error response structure
 */
export interface SnowflakeApiError {
  message?: string;
  detail?: string | ValidationErrorDetail[];
  error?: string;
  // Fields for single validation error objects
  type?: string;
  loc?: string[];
  msg?: string;
}

/**
 * Snowflake Account API service
 * Handles all Snowflake account operations including managed key authentication
 */
export const snowflakeApi = {
  /**
   * Create a managed key Snowflake account
   *
   * Backend generates RSA key pair, stores private key securely,
   * and returns public key for user to add to Snowflake.
   *
   * @param accountData - Account credentials without private key
   * @returns Promise with account ID and public key
   *
   * @example
   * ```typescript
   * const result = await snowflakeApi.createManagedKeyAccount({
   *   type: 'managed_pkey',
   *   account: 'myaccount',
   *   username: 'myuser',
   *   warehouse: 'COMPUTE_WH',
   *   role: 'ACCOUNTADMIN'
   * });
   * // Use result.public_key to add to Snowflake
   * ```
   *
   * Backend Requirements:
   * - Endpoint: POST /api/v1/snowflake/account
   * - Generate 2048-bit RSA key pair
   * - Store private key encrypted in database
   * - Return public key in PEM format
   * - Return SHA256 fingerprint of public key
   * - Assign proper permissions to user
   *
   * TODO: Backend endpoint needs to be implemented
   * See: service/snowflake_accounts/api.py
   * See: service/snowflake_accounts/schemas.py (add SnowflakeManagedPkeyAccount)
   * See: service/snowflake_accounts/service.py (add create_connector_from_managed_pkey)
   */
  async createManagedKeyAccount(accountData: ManagedKeyAccountRequest): Promise<ManagedKeyAccountResponse> {
    try {
      const response = await axios.post<ManagedKeyAccountResponse>('/api/v1/snowflake/account', accountData);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create managed key account:', error);
      throw error;
    }
  },

  /**
   * Confirm managed key account setup
   *
   * Tests Snowflake connection after user has added the public key.
   * Verifies that authentication works correctly.
   *
   * @param accountId - The Snowflake account ID from createManagedKeyAccount
   * @returns Promise that resolves if connection succeeds
   *
   * @example
   * ```typescript
   * await snowflakeApi.confirmManagedKeyAccount(123);
   * // Connection confirmed, proceed to next step
   * ```
   *
   * Backend Requirements:
   * - Endpoint: POST /api/v1/snowflake/account/{id}/confirm
   * - Retrieve account from database
   * - Attempt Snowflake connection using stored private key
   * - Run test query (e.g., SELECT CURRENT_USER())
   * - Return success/failure
   * - Update account status if needed
   *
   * TODO: Backend endpoint needs to be implemented
   */
  async confirmManagedKeyAccount(accountId: number): Promise<void> {
    try {
      const response = await axios.post<ConfirmManagedKeyResponse>(`/api/v1/snowflake/account/${accountId}/confirm`);

      // Check if the response indicates an error even with 200 status
      if (response.data.status === 'error') {
        const errorMessage = response.data.message ?? 'Connection test failed';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Failed to confirm managed key account:', error);
      throw error;
    }
  },

  /**
   * Rotate managed keys for an account
   *
   * Generates new RSA key pair and returns new public key.
   * User must add new public key to Snowflake and remove old one.
   *
   * @param accountId - The Snowflake account ID
   * @returns Promise with new public key
   *
   * @example
   * ```typescript
   * const result = await snowflakeApi.rotateManagedKeys(123);
   * // Use result.public_key to update Snowflake
   * ```
   *
   * Backend Requirements:
   * - Endpoint: POST /api/v1/snowflake/account/{id}/rotate-keys
   * - Generate new 2048-bit RSA key pair
   * - Keep old key temporarily for rollback
   * - Update database with new private key
   * - Return new public key
   * - Log rotation event for audit
   *
   * TODO: Backend endpoint needs to be implemented (future feature)
   */
  async rotateManagedKeys(accountId: number): Promise<RotateManagedKeysResponse> {
    try {
      const response = await axios.post<RotateManagedKeysResponse>(
        `/api/v1/snowflake/account/${accountId}/rotate-keys`,
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to rotate managed keys:', error);
      throw error;
    }
  },

  /**
   * Validate a Snowflake account identifier
   *
   * Checks if a Snowflake account identifier exists by making a validation request.
   * This should be called before creating an account record.
   *
   * @param identifier - The Snowflake account identifier to validate
   * @returns Promise with validation result
   *
   * @example
   * ```typescript
   * const result = await snowflakeApi.validateSnowflakeAccount('myaccount');
   * if (result.valid) {
   *   // Proceed with account creation
   * }
   * ```
   *
   * Backend Requirements:
   * - Endpoint: GET /api/v1/snowflake/validate-account/{identifier}
   * - Makes HEAD request to Snowflake to verify account exists
   * - Returns validation status and message
   */
  async validateSnowflakeAccount(identifier: string): Promise<ValidateAccountResponse> {
    try {
      const response = await axios.get<ValidateAccountResponse>(`/api/v1/snowflake/validate-account/${identifier}`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to validate Snowflake account:', error);
      throw error;
    }
  },

  /**
   * Update a managed key Snowflake account
   *
   * Updates account object names (username, warehouse, role) without regenerating keys.
   * Used when user edits object names inline in the UI.
   *
   * @param accountId - The Snowflake account ID
   * @param updates - Object containing fields to update
   * @returns Promise that resolves when update is complete
   *
   * @example
   * ```typescript
   * await snowflakeApi.updateManagedKeyAccount(123, {
   *   username: 'NEW_USER',
   *   warehouse: 'NEW_WH',
   *   role: 'NEW_ROLE'
   * });
   * ```
   *
   * Backend Requirements:
   * - Endpoint: PUT /api/v1/snowflake/account/{id}
   * - Update account record with new object names
   * - Do not regenerate keys
   * - Return success response
   */
  async updateManagedKeyAccount(
    accountId: number,
    updates: Omit<UpdateManagedKeyAccountRequest, 'type'>,
  ): Promise<void> {
    try {
      await axios.put(`/api/v1/snowflake/account/${accountId}`, {
        type: 'managed_pkey',
        ...updates,
      });
    } catch (error: any) {
      console.error('Failed to update managed key account:', error);
      throw error;
    }
  },

  /**
   * Get managed key account details
   *
   * Retrieves account details for state restoration when users navigate back
   * or refresh the page during onboarding.
   *
   * @param accountId - The Snowflake account ID
   * @returns Promise with account details
   */
  async getManagedKeyAccount(accountId: number): Promise<ManagedKeyAccountDetailsResponse> {
    try {
      const response = await axios.get<ManagedKeyAccountDetailsResponse>(
        `/api/v1/snowflake/managed-key-account/${accountId}`,
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get managed key account:', error);
      throw error;
    }
  },
};

/**
 * Type guard to check if error is a Snowflake API error
 */
export function isSnowflakeApiError(error: unknown): error is { response: { data: SnowflakeApiError } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object' &&
    (error as { response?: { data?: unknown } }).response !== null &&
    'data' in ((error as { response: unknown }).response as object)
  );
}

/**
 * Extract user-friendly error message from Snowflake API error
 */
export function getSnowflakeErrorMessage(error: unknown): string {
  if (isSnowflakeApiError(error)) {
    const apiError = error.response.data;
    // Handle string responses directly
    if (typeof apiError === 'string') {
      return apiError;
    }

    // Handle FastAPI/Pydantic validation errors (422 responses)
    // These come as { detail: [{ type, loc, msg }] }
    if (apiError.detail !== undefined && Array.isArray(apiError.detail)) {
      const validationErrors = apiError.detail
        .map((err: { loc?: string[]; msg?: string; type?: string }) => {
          const location = err.loc !== undefined ? err.loc.join(' -> ') : '';
          const message = err.msg ?? err.type ?? 'Validation error';
          return location !== '' ? `${location}: ${message}` : message;
        })
        .join('; ');
      return validationErrors !== '' ? validationErrors : 'Validation error occurred';
    }

    // Handle single validation error object
    if (apiError.type !== undefined && apiError.msg !== undefined) {
      const location =
        apiError.loc !== null && apiError.loc !== undefined && Array.isArray(apiError.loc)
          ? apiError.loc.join(' -> ')
          : '';
      const message = apiError.msg ?? apiError.type;
      return location !== '' ? `${location}: ${message}` : message;
    }

    return apiError.message ?? apiError.detail ?? apiError.error ?? 'An error occurred';
  }

  // For generic Error instances, extract the message
  if (error instanceof Error) {
    return error.message !== '' ? error.message : 'An unexpected error occurred';
  }

  return 'An unexpected error occurred';
}
